import { CircularProgress } from '@mui/material'
import { GET_USER_INFO, getUserInfo, patchUserInfo } from 'api'
import { Button, Input, InputPhoneFull } from 'components'
import { MenuContext } from 'context'
import { useToast } from 'hooks'
import { UserDataFormInputs } from 'interfaces'
import React, { useContext, useEffect, useState } from 'react'
import { SubmitHandler, useForm, UseFormSetValue } from 'react-hook-form'
import { useQuery } from 'react-query'
import Cookies from 'universal-cookie'
import { parseToDisplay, parseToStorage, PHONE_PLACEHOLDER } from 'utils'

export const UserDataForm = (): JSX.Element => {
	const cookies = new Cookies()
	const userId = cookies.get('userId')

	const { showToast } = useToast()
	const { setFullName } = useContext(MenuContext)
	const [loading, setLoading] = useState(false)
	const { data, refetch, isLoading } = useQuery([GET_USER_INFO, userId], () =>
		getUserInfo(userId)
	)
	const {
		watch,
		register,
		setValue,
		handleSubmit,
		reset,
		formState: { errors, isDirty, isValid }
	} = useForm<UserDataFormInputs>({
		mode: 'all',
		defaultValues: {
			name: '',
			phoneNumber: '',
			email: ''
		}
	})

	const email = watch('email')
	const phoneNumber = watch('phoneNumber')

	const rules = {
		name: {
			required: { value: true, message: 'This field is required' },
			maxLength: { value: 20, message: 'Maximum characters exceeded' }
		},
		phoneNumber: {
			required: { value: true, message: 'This field is required' },
			pattern: {
				value: /^\+\d{1,3} \(\d{3}\) \d{3}-\d{4}$/,
				message: 'Invalid phone number'
			}
		},
		email: {
			required: { value: true, message: 'This field is required' },
			maxLength: { value: 60, message: 'Maximum characters exceeded' }
		}
	}

	const onSubmit: SubmitHandler<UserDataFormInputs> = async values => {
		setLoading(true)
		try {
			const response = await patchUserInfo({
				full_name: values.name,
				phone_number: parseToStorage(values.phoneNumber),
				email: values.email
			})
			if (response?.message) showToast(response.message, '', 'success')
			setFullName(values.name)
			await refetch()
			reset({
				name: values.name,
				email: values.email,
				phoneNumber: values.phoneNumber
			})
		} catch (error) {
			console.error('Error', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (data) {
			setValue('email', data.email, { shouldValidate: true })
			setValue('name', data.full_name, { shouldValidate: true })
			setValue('phoneNumber', parseToDisplay(data.phone_number ?? ''), {
				shouldValidate: true
			})
		}
	}, [data])

	const customSetValue: UseFormSetValue<UserDataFormInputs> = (
		name,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		value: any
	) => {
		setValue(name, value, {
			shouldValidate: true,
			shouldDirty: true
		})
	}

	return (
		<div className="h-[90%]">
			{(isLoading || loading) && (
				<div className="flex items-center justify-center w-full h-[400px] py-2 px-1 ">
					<CircularProgress disableShrink />
				</div>
			)}
			{!isLoading && !loading && data && (
				<form
					onSubmit={handleSubmit(onSubmit)}
					className="mt-5 h-full flex flex-col justify-between"
				>
					<div>
						<Input
							name="name"
							title="Name"
							register={register}
							rules={rules.name}
							placeholder="Name"
							error={errors.name}
							className="mb-4"
							min={0}
						/>
						<InputPhoneFull
							name="phoneNumber"
							title="Phone Number"
							register={register}
							setValueInput={customSetValue}
							rules={rules.phoneNumber}
							error={errors.phoneNumber}
							placeholder={PHONE_PLACEHOLDER}
							watchValue={phoneNumber}
						/>
						<Input
							name="email"
							title="E-mail"
							className="mt-4"
							type="email"
							register={register}
							rules={rules.email}
							placeholder="example@gmail.com"
							error={errors.email}
							limit
							watchValue={email}
							setValueInput={setValue}
						/>
					</div>
					<div className="flex justify-center md:justify-end pt-8">
						<Button
							label="Save"
							type="submit"
							color="Primary"
							disabled={!isDirty || !isValid}
						/>
					</div>
				</form>
			)}
		</div>
	)
}
